import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard,
  Bot,
  MessageSquare,
  Users,
  BarChart3,
  Store,
  CreditCard,
  Settings,
  LogOut,
  BrainCircuit,
  Sparkles,
  ArrowLeftRight,
  Shield,
  Menu,
  X,
  Search,
  Inbox,
  LifeBuoy,
  CalendarDays,
  Handshake,
  Contact,
  FileSignature,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAdminMode } from "@/hooks/use-admin-mode";
import { getBillingStatus } from "@/lib/billing.functions";
import { getPendingActions } from "@/lib/pending.functions";
import { Logo } from "@/components/marketing/Logo";
import { CommandPalette } from "@/components/CommandPalette";
import { NotificationCenter } from "@/components/NotificationCenter";
import { LanguageSwitcher } from "@/components/marketing/LanguageSwitcher";
import { TwinSwitcher } from "@/components/TwinSwitcher";
import { motion } from "framer-motion";

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  professional: "Pro",
  scale: "Scale",
};
const planLabel = (p: string) => PLAN_LABELS[p] ?? p;

const allItems = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, alwaysVisible: true },
  { to: "/twin", key: "twin", icon: Bot },
  { to: "/knowledge", key: "knowledge", icon: BrainCircuit },
  { to: "/conversations", key: "conversations", icon: MessageSquare },
  { to: "/leads", key: "leads", icon: Users },
  { to: "/crm", key: "crm", icon: Contact },
  { to: "/requests", key: "requests", icon: Inbox },
  { to: "/appointments", key: "appointments", icon: CalendarDays },
  { to: "/opportunities", key: "opportunities", icon: Sparkles },
  { to: "/deals", key: "deals", icon: Handshake },
  { to: "/exchange", key: "exchange", icon: ArrowLeftRight },
  { to: "/analytics", key: "analytics", icon: BarChart3 },
  { to: "/marketplace", key: "marketplace", icon: Store },
  { to: "/contracts", key: "contracts", icon: FileSignature },

  { to: "/billing", key: "billing", icon: CreditCard },
  { to: "/help", key: "help", icon: LifeBuoy, alwaysVisible: true },
  { to: "/settings", key: "settings", icon: Settings, alwaysVisible: true },
] as const;

/** Count of things waiting in a section. Hidden at zero — a badge showing "0"
 *  is noise, and a permanent badge stops meaning anything. */
function NavBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="ml-auto min-w-[20px] rounded-full bg-[oklch(0.82_0.18_168)] px-1.5 py-0.5 text-center text-[11px] font-medium leading-none text-black">
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function DashboardShell({
  children,
  title,
  subtitle,
  actions,
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hasAccess = true;
  const { viewAsUser, setViewAsUser } = useAdminMode();
  const { t } = useTranslation();

  // Wait for the Supabase session to hydrate before firing authed serverFns —
  // otherwise the bearer attacher sends no token and the request 401s.
  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Real plan + usage for the sidebar card (no invented numbers).
  const fetchBilling = useServerFn(getBillingStatus);
  const { data: billing } = useQuery({
    queryKey: ["shell-billing"],
    queryFn: () => fetchBilling(),
    staleTime: 60_000,
    retry: false,
    enabled: hasSession,
  });

  // What is waiting on this user. Shown as nav badges so a pending signature is
  // visible in the product, not only in an email that may never be opened.
  const fetchPending = useServerFn(getPendingActions);
  const { data: pending } = useQuery({
    queryKey: ["shell-pending"],
    queryFn: () => fetchPending(),
    staleTime: 30_000,
    retry: false,
    enabled: hasSession,
  });
  const badgeFor = (key: string): number => {
    if (!pending) return 0;
    if (key === "contracts") return pending.contracts;
    if (key === "deals") return pending.deals;
    if (key === "opportunities") return pending.opportunities + pending.radar;
    return 0;
  };

  // Real admin detection — RLS lets users read only their own roles.
  const { data: adminRows } = useQuery({
    queryKey: ["shell-is-admin"],
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("role").eq("role", "admin");
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    retry: false,
    enabled: hasSession,
  });

  const isAdmin = (adminRows?.length ?? 0) > 0;

  const items = allItems.filter(
    (item) => hasAccess || (item as { alwaysVisible?: boolean }).alwaysVisible,
  );

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  // Mobile navigation: the sidebar only exists from lg upwards, so below
  // that a slide-over drawer carries the full nav. Closes on route change.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <CommandPalette companyId={(billing as any)?.companyId} />
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-cream border-r border-border flex flex-col shadow-2xl">
            <div className="px-5 py-5 flex items-center justify-between">
              <Logo size={18} />
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="p-2 rounded-lg hover:bg-card text-muted-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-3 pb-2">
              <TwinSwitcher />
            </div>
            <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setViewAsUser(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] text-foreground bg-card border border-border"
                >
                  <Shield className="h-4 w-4" /> Admin Console
                </Link>
              )}
              {items.map((item) => {
                const active = pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] transition ${
                      active
                        ? "bg-card text-foreground border border-border shadow-[var(--shadow-card)]"
                        : "text-muted-foreground hover:bg-card hover:text-foreground border border-transparent"
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {t(`app.shell.nav.${item.key}`)}
                    <NavBadge count={badgeFor(item.key)} />
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t border-border">
              {billing && (
                <div className="rounded-2xl border border-border bg-card p-3 text-[12px]">
                  <div className="font-medium text-foreground">{planLabel(billing.plan)} plan</div>
                  <div className="text-muted-foreground">
                    {billing.used.toLocaleString()} / {billing.limit.toLocaleString()} conversations
                  </div>
                </div>
              )}
              <button
                onClick={signOut}
                className="mt-3 w-full flex items-center gap-2 px-3 py-2 text-[14px] text-muted-foreground hover:text-foreground rounded-xl hover:bg-card transition"
              >
                <LogOut className="h-4 w-4" /> {t("app.shell.signOut")}
              </button>
            </div>
          </div>
        </div>
      )}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-cream sticky top-0 h-screen">
        <div className="px-6 py-6">
          <Logo size={18} />
        </div>
        <div className="px-3 pb-2">
          <TwinSwitcher />
        </div>
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setViewAsUser(false)}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 text-[13.5px] transition ${
                pathname === "/admin"
                  ? "bg-foreground text-background border border-foreground"
                  : "text-foreground bg-card hover:bg-cream border border-border"
              }`}
            >
              <Shield className="h-4 w-4" />
              Admin Console
            </Link>
          )}
          {items.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-[13.5px] transition ${
                  active
                    ? "bg-card text-foreground border border-border shadow-[var(--shadow-card)]"
                    : "text-muted-foreground hover:bg-card hover:text-foreground border border-transparent"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {t(`app.shell.nav.${item.key}`)}
                <NavBadge count={badgeFor(item.key)} />
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          {billing && (
            <div className="rounded-2xl border border-border bg-card p-3 text-[12px]">
              <div className="font-medium mb-1 text-foreground">{planLabel(billing.plan)} plan</div>
              <div className="text-muted-foreground">
                {billing.used.toLocaleString()} / {billing.limit.toLocaleString()} conversations
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-border overflow-hidden">
                <div
                  className="h-full bg-foreground transition-all"
                  style={{
                    width: `${Math.min(100, Math.round((billing.used / Math.max(1, billing.limit)) * 100))}%`,
                  }}
                />
              </div>
              {billing.plan !== "scale" && (
                <Link
                  to="/billing"
                  className="mt-3 block text-center rounded-full bg-foreground text-background font-medium py-1.5"
                >
                  {t("app.shell.upgrade")}
                </Link>
              )}
            </div>
          )}
          <button
            onClick={signOut}
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 text-[13.5px] text-muted-foreground hover:text-foreground rounded-xl hover:bg-card transition"
          >
            <LogOut className="h-4 w-4" /> {t("app.shell.signOut")}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {isAdmin && viewAsUser && (
          <div className="bg-foreground text-background text-xs px-6 sm:px-10 py-2 flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" /> Viewing as normal user (admin mode)
            </span>
            <button
              onClick={() => {
                setViewAsUser(false);
                navigate({ to: "/admin" });
              }}
              className="underline underline-offset-2 hover:opacity-80"
            >
              Return to Admin Console
            </button>
          </div>
        )}
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
          <div className="px-4 sm:px-10 py-5 sm:py-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                className="lg:hidden shrink-0 p-2 -ml-1 rounded-lg border border-border bg-card text-foreground"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h1 className="font-serif text-[24px] sm:text-[34px] leading-[1.05] tracking-[-0.025em] truncate">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-[14px] text-muted-foreground mt-1 truncate">{subtitle}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() =>
                  window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }))
                }
                title="Search & commands (Ctrl+K)"
                className="hidden sm:flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition"
              >
                <Search className="h-3.5 w-3.5" />
                <kbd className="font-mono text-[10px] border border-border rounded px-1 py-0.5">
                  ⌘K
                </kbd>
              </button>
              <NotificationCenter />
              {actions}
              <LanguageSwitcher />
            </div>
          </div>
        </header>
        <main className="px-6 sm:px-10 py-8">
          <motion.div
            key={pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
